import type { DeviceMetric } from './deviceMetricTypes';
import { apiService } from '../config/api';
import { CollectionSourceId, collectionSources } from '../config/collectionSources';

export type FitbitReadMode = 'visualization' | 'coverage' | 'adherence' | 'export' | 'alerts';
export type FitbitAvailableRange = { startDate: string; endDate: string };
// UTC days with watch data. Daily calorie estimates arrive without a sync, so they are not counted here.
export type FitbitWear = { lastHeartRateDate: string | null; lastStepsDate: string | null;
  recentHeartRateDays: string[]; recentStepsDays: string[] };
// The participant's current collection state, read fresh on every availability check.
export type FitbitCollectionStatus = { state: 'queued' | 'active' | 'deferred' | 'completed' | 'held_for_review' | 'connection_action_required' | 'cancelled';
  since: string | null; failureCode: string | null;
  failureKind: 'transient' | 'rate_limit' | 'retry_exhausted' | 'review_required' | 'connection_action_required' | null;
  httpStatus: number | null; autoRetry: boolean; autoRetryAfter: string | null };
export type FitbitParticipantAvailability = { range: FitbitAvailableRange | null; indexedAt: string; wear?: FitbitWear | null;
  collection?: FitbitCollectionStatus | null };
export type FitbitHistoryProgress = {
  historyStart: string; historyEnd: string; state: string; controlState: string;
  percentage: number; completedWindows: number; updatedAt: string;
  checkedStart?: string | null; checkedEnd?: string | null;
  // Sources that can collect on request also report their daily check.
  recentCheckRunning?: boolean; lastCheckedAt?: string | null;
  // How the latest requested check ended, where the source reports it.
  recentCheckState?: string | null;
};
export type RecentCollectionRequest = { status: 'queued' | 'collecting' | 'recent';
  requestedAt?: string; checkedAt?: string; startDate?: string; endDate?: string };
export type FitbitReadParticipant = { participantId: string; name: string; enrollmentStatus: string;
  trackingId?: string | null;
  availabilityStatus?: 'checking' | 'ready' | 'error';
  dataAvailability?: FitbitParticipantAvailability | null };
// Compatibility alias for existing collection service consumers.
export type FitbitMetric = DeviceMetric;
export type FitbitPoint = { date: string; unit: string | null; samples: number; minimum: number;
  maximum: number; average: number; total: number };
export type FitbitSummary = {
  catalog: FitbitMetric[];
  connectionStatus: string;
  timezone: string;
  campaigns: Array<{ state: string; historyStart: string; historyEndExclusive: string;
    completedWindows: number; runningWindows: number; failureCode: string | null }>;
  checkedWindows: Array<{ start: string; endExclusive: string }>;
  metrics: Array<{ routeKey: string; presentRecords: number; absentRecords: number;
    firstRecord: string | null; lastRecord: string | null; firstLocalDate?: string | null; lastLocalDate?: string | null }>;
  dailyCoverage: Array<{ date: string; routeKey: string; presentRecords: number; absentRecords: number }>;
  adherence: { days: Array<{ date: string; checked: boolean; completeDay: boolean; steps: number | null;
    heartRateMinutes: number | null; adherence: number | null;
    evaluations: Array<{ metric: string; threshold: number; value: number | null; percent: number | null }> }>;
    unsupportedThresholds: string[]; supportedThresholds: string[]; thresholds: Array<{ metric: string; value: number }>; coverageDefinition: string };
};
export type FitbitStoredRecord = { routeKey: string; dataType: string; temporalKind: string;
  bucketAt: string; observedAt: string | null; observedEndAt: string | null;
  observedDate: string | null; observedEndDate: string | null; nativeTime: unknown;
  valueState: 'present' | 'absent'; measurement: unknown; sleepComponents?: Array<{
    kind: string; stage: string | null; startTime: string | null; endTime: string | null;
    nativeTime: unknown; durationMinutes: number | null; segmentCount: number | null;
    startUtcOffsetSeconds: number | null; endUtcOffsetSeconds: number | null;
  }> };
export type FitbitRecordsPage = { records: FitbitStoredRecord[]; nextOffset: number | null; revision: string };
export type FitbitAvailability = FitbitParticipantAvailability & {
  metrics: FitbitSummary['metrics']; catalog: FitbitMetric[]; checkedAt: string; timezone: string;
  coverage?: { pendingRefresh: boolean; refreshedAt: string | null; wear: FitbitWear } | null;
  collection?: FitbitCollectionStatus | null;
};
export type FitbitAlertState = { date: string; version: number; status: 'pending' | 'snoozed' | 'ignored' | 'resolved' | 'contacted';
  snoozedUntil: string | null; lastContactAt: string | null; contact: { status: 'sending' | 'sent' | 'unconfirmed' | 'not_sent'; requestedAt: string } | null;
  history: Array<{ requestId: string; action: string; actorId: string; at: string; note: string; score?: number | null; deliveryResult?: string }> };
export type FitbitAlertAction = 'note' | 'snooze' | 'ignore' | 'resolve' | 'reopen' | 'contact' | 'review_delivery';
export type FitbitAlertTemplate = { id: string; name: string; subject: string; body: string };
export type FitbitContactPreview = { to: string; subject: string; text: string };

export function createCollectionDataService(source: Exclude<CollectionSourceId, 'healthkit'> = 'fitbit') {
  const root = collectionSources[source].apiBase;
  function readFitbitHistoryProgress(studyId: string, participantId: string): Promise<{
    progress: FitbitHistoryProgress | null; checkedAt: string;
  }> {
    return apiService.get(`${base(studyId)}/${encodeURIComponent(participantId)}/history-progress`);
  }

  // Checks today and yesterday now instead of after the participant's next local midnight.
  function requestRecentCollection(studyId: string, participantId: string): Promise<RecentCollectionRequest> {
    return apiService.post(`${base(studyId)}/${encodeURIComponent(participantId)}/recent-collection`, {});
  }

  function readFitbitAvailability(studyId: string, participantId: string): Promise<FitbitAvailability> {
    return apiService.get(`${base(studyId)}/${encodeURIComponent(participantId)}/availability`);
  }

  const base = (studyId: string) => `${root}/studies/${encodeURIComponent(studyId)}/participants`;

  function readFitbitAlertStates(studyId: string, participantId: string, range: { startDate: string; endDate: string }): Promise<{ states: FitbitAlertState[] }> {
    return apiService.get(`${base(studyId)}/${encodeURIComponent(participantId)}/alert-decisions?${new URLSearchParams(range)}`);
  }

  function readFitbitAlertTemplates(studyId: string): Promise<{ templates: FitbitAlertTemplate[]; contactEnabled: boolean }> {
    return apiService.get(`${root}/studies/${encodeURIComponent(studyId)}/alert-templates`);
  }

  function updateFitbitAlert(studyId: string, participantId: string, date: string, payload: {
    action: FitbitAlertAction; expectedVersion: number; requestId: string; note?: string; minutes?: number; subject?: string; body?: string; deliveryResult?: 'sent' | 'not_sent';
  }): Promise<{ state: FitbitAlertState; replayed?: boolean }> {
    return apiService.post(`${base(studyId)}/${encodeURIComponent(participantId)}/alerts/${encodeURIComponent(date)}/actions`, payload);
  }

  function previewFitbitContact(studyId: string, participantId: string, date: string, subject: string, body: string): Promise<{ message: FitbitContactPreview }> {
    return apiService.post(`${base(studyId)}/${encodeURIComponent(participantId)}/alerts/${encodeURIComponent(date)}/contact-preview`, { subject, body });
  }

  function readFitbitStudyStatus(studyId: string): Promise<{ runningJobs: number; queuedJobs: number; heldJobs: number }> {
    return apiService.get(`${root}/studies/${encodeURIComponent(studyId)}/status`);
  }

  async function loadFitbitParticipants(studyId: string): Promise<{
    participants: FitbitReadParticipant[]; hasLegacyParticipants: boolean; hasOutdatedFitbitParticipants?: boolean; catalog: FitbitMetric[];
  }> {
    const participants: FitbitReadParticipant[] = [];
    let catalog: FitbitMetric[] = [];
    let after: string | null = null;
    let hasLegacyParticipants = false;
    let hasOutdatedFitbitParticipants = false;
    const seen = new Set<string>();
    do {
      const page: { participants: FitbitReadParticipant[]; nextCursor: string | null; hasLegacyParticipants: boolean; hasOutdatedFitbitParticipants?: boolean; catalog?: FitbitMetric[] } =
        await apiService.get(`${base(studyId)}${after ? `?after=${encodeURIComponent(after)}` : ''}`);
      participants.push(...page.participants);
      if (page.catalog) catalog = page.catalog;
      hasLegacyParticipants ||= page.hasLegacyParticipants;
      hasOutdatedFitbitParticipants ||= Boolean(page.hasOutdatedFitbitParticipants);
      after = page.nextCursor;
      if (after && (seen.has(after) || seen.size >= 50)) throw new Error('Participant list is too large; narrow the study view.');
      if (after) seen.add(after);
    } while (after);
    return { participants, hasLegacyParticipants, hasOutdatedFitbitParticipants, catalog };
  }

  function readFitbitData<T>(studyId: string, participantId: string, action: 'summary' | 'series' | 'records',
    query: Record<string, string>): Promise<T> {
    return apiService.get(`${base(studyId)}/${encodeURIComponent(participantId)}/${action}?${new URLSearchParams(query)}`);
  }

  async function exportFitbitRecords(studyId: string, participantId: string,
    query: Record<string, string>, onProgress: (count: number) => void, signal?: AbortSignal): Promise<Blob> {
    const chunks: string[] = [];
    let offset: number | null = 0;
    let count = 0;
    let bytes = 0;
    let revision: string | undefined;
    do {
      if (signal?.aborted) throw new Error('Export cancelled');
      const page: { records: FitbitStoredRecord[]; nextOffset: number | null; revision: string } = await readFitbitData(
        studyId, participantId, 'records', { ...query, offset: String(offset), ...(revision ? { revision } : {}) });
      if (signal?.aborted) throw new Error('Export cancelled');
      const chunk = page.records.map(record => JSON.stringify(record) + '\n').join('');
      bytes += new Blob([chunk]).size;
      if (bytes > 50 * 1024 * 1024) throw new Error('Select a smaller date range or one metric to export at most 50 MB.');
      if (!/^[a-f0-9]{32}$/.test(page.revision) || (revision && revision !== page.revision)) {
        throw new Error('Collection changed during export. Please retry.');
      }
      revision = page.revision;
      chunks.push(chunk);
      count += page.records.length;
      onProgress(count);
      if (page.nextOffset !== null && page.nextOffset <= offset) throw new Error('Export pagination did not advance');
      offset = page.nextOffset;
      if (count > 100000) throw new Error('Select a smaller date range or one metric to export at most 100,000 records.');
    } while (offset !== null);
    return new Blob(chunks, { type: 'application/x-ndjson' });
  }
  return { readFitbitHistoryProgress, requestRecentCollection, readFitbitAvailability, readFitbitAlertStates, readFitbitAlertTemplates, updateFitbitAlert, previewFitbitContact, readFitbitStudyStatus, loadFitbitParticipants, readFitbitData, exportFitbitRecords };
}

export const { readFitbitHistoryProgress, requestRecentCollection, readFitbitAvailability, readFitbitAlertStates, readFitbitAlertTemplates, updateFitbitAlert, previewFitbitContact, readFitbitStudyStatus, loadFitbitParticipants, readFitbitData, exportFitbitRecords } = createCollectionDataService();
